import { clone } from "ramda";

import * as actionTypes from '../actions/actionTypes';
import { updateObject } from "../../lib/objectUtilities";
import { NotificationInfo } from "../../lib/types";

const initialState = {
  loaded: false,
  id: 0,
  name: "",
  alliance: null,
  membershipApplication: null,
  race: { id: 0, name: "unknown" },
  commander: false,
  land: 0,
  landPosition: 0,
  power: 0,
  powerPosition: 0,
  ljpPoints: 0,
  rating: 0,
  lastAction: null,
  note: "",
  player: null,
  resources: {
    grain: 0,
    gold: 0,
    stone: 0,
    iron: 0,
    mana: 0,
    bricks: 0,
    weapons: 0,
    "power-points": 0,
  },
  resourceBalances: null,
  resourcesLimits: null,
  marketItems: null,
  extraMarketSlots: 0,
  marketPlaceBlock: 0,
  population: null,
  happiness: 0,
  development: null,
  turn: 0,
  maxTurns: 0,
  turnBank: 0,
  turnBankExpiration: 0,
  labyrinthAttempt: 0,
  labyrinthAttemptLimit: 2,
  canDoLabyrinth: false,
  allBuildings: null,
  slots: null,
  slotPrices: null,
  generals: null,
  readyToMagic: false,
  spells: null,
  magicClosed: false,
  magicCore: null,
  spellCastLimit: 0,
  magicMode: 0,
  magicModeAftermath: 0,
  spellsLimit: 0,
  spellUpgradeAvailable: false,
  opponent: null,
  war: null,
  readyToAttack: false,
  army: null,
  armyFormation: null,
  readyToThieves: false,
  canSendCounterintelligence: false,
  crimes: null,
  crimesLimit: 0,
  limitingCrimes: 0,
  crimeTypes: null,
  allQuestsFinished: false,
  hasFinishedQuest: false,
  tutorialFinished: false,
  availableQuests: null,
  finishedQuests: null,
  turnNews: null,
  famine: false,
  bankruptcy: false,
  postWarInformation: false,
  preWarPopulation: 0,
  news: null,
  gameDay: null,
  unitNames: null,
  upgrades: null,
  outlinePalace: false,
  armyEmperorTent: false,
  magicEmperorTent: false,
  thievesEmperorTent: false,
  newPosts: null,
  chatPosts: null,
  mail: null
};

const setState = (state: any, action: any) => ({
  ...state,
  ...action.initialState,
  loaded: true
});

const updateState = (state: any, action: any) => ({
  ...state,
  ...action.updatedState
});

const updateResources = (state: any, action: any) => ({
  ...state,
  resources: action.resources || state.resources,
  resourceBalances: action.resourceBalances || state.resourceBalances
});

const updateSlot = (state: any, action: any) => {
  const slotChains = action.slotChains || clone(state.slotChains);

  const slotChainKey = Object.keys(slotChains)
    .find((key: any) => Object.keys(slotChains[key])
      .some((k: string) => (k === "slots" || !Number.isNaN(k))
          && typeof slotChains[key][k] === "object"
          && Object.keys(slotChains[key][k])
            .some((slotId: string) => parseInt(slotId, 10) === parseInt(action.slotId, 10)))) || "0";

  if (action.slotUpdate.turnsToUpgrade > 0) {
    const slotChain = clone(slotChains[slotChainKey]);
    slotChain.plus = true;
    slotChains[slotChainKey] = slotChain;
  }

  const categoryKey = Object.keys(slotChains[slotChainKey])
    .find((key: any) => typeof slotChains[slotChainKey][key] === "object"
      && Object.keys(slotChains[slotChainKey][key])
        .some(k => parseInt(k, 10) === parseInt(action.slotId, 10))) || "slots";

  const slot = categoryKey && clone(slotChains[slotChainKey][categoryKey][action.slotId]);

  slotChains[slotChainKey][categoryKey][action.slotId] = updateObject(slot, action.slotUpdate);

  return {
    ...state,
    slotChains
  }
};

const updatePopulation = (state: any, action: any) => {
  const population = clone(state.population);

  return {
    ...state,
    population: updateObject(population, action.populationUpdate)
  }
}

const updateDevelopment = (state: any, action: any) => {
  const development = clone(state.development);

  return {
    ...state,
    development: updateObject(development, action.developmentUpdate)
  }
}

const updateGeneral = (state: any, action: any) => {
  const generals = clone(state.generals);
  const generalIndex = Object.keys(generals)
    .find(key => generals[key].general && generals[key].general.id === action.general.general.id);

  if (generalIndex) {
    generals[generalIndex] = updateObject(generals[generalIndex], action.general);
  }

  return {
    ...state,
    generals
  }
}

const updateGenerals = (state: any, action: any) => {
  const generals = clone(state.generals);

  return {
    ...state,
    generals: updateObject(generals, action.generals)
  }
}

const removeNotification = (state: any, action: any) => {
  const player = clone(state.player);
  const { notifications } = player;
  const notificationIndex = notifications
    .findIndex((n: NotificationInfo) => n.id === action.notId);
  if (notificationIndex !== -1) {
    notifications.splice(notificationIndex, 1);
  }
  return {
    ...state,
    player: updateObject(player, notifications)
  }
}

const updateNotificationsSeen = (state: any) => {
  const player = clone(state.player);
  const notifications = player.notifications
    .map((notification: NotificationInfo) => notification.unseen = false);

  return {
    ...state,
    player: updateObject(player, notifications)
  }
}

const updateCredits = (state: any, action: any) => {
  const player = clone(state.player);
  player.credits = action.credits;

  return {
    ...state,
    player
  }
}

const updateNews = (state: any, action: any) => {
  const news = clone(state.news);
  return {
    ...state,
    news: updateObject(news, action.newsUpdate)
  }
}


const reducer = (state = initialState, action: any) => {
  switch (action.type) {
    case actionTypes.SET_STATE: return setState(state, action);
    case actionTypes.UPDATE_STATE: return updateState(state, action);
    case actionTypes.UPDATE_POPULATION: return updatePopulation(state, action);
    case actionTypes.UPDATE_DEVELOPMENT: return updateDevelopment(state, action);
    case actionTypes.UPDATE_RESOURCES: return updateResources(state, action);
    case actionTypes.UPDATE_SLOT: return updateSlot(state, action);
    // general
    case actionTypes.UPDATE_GENERAL: return updateGeneral(state, action);
    case actionTypes.UPDATE_GENERALS: return updateGenerals(state, action);
    case actionTypes.REMOVE_NOTIFICATION: return removeNotification(state, action);
    case actionTypes.UPDATE_NOTIFICATIONS_SEEN: return updateNotificationsSeen(state);
    case actionTypes.UPDATE_CREDITS: return updateCredits(state, action);
    case actionTypes.UPDATE_NEWS: return updateNews(state, action);
    default: return state;
  }
};

export default reducer;
